import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventForm = ({ event, rooms, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    room_id: '',
    date: new Date(),
    start_time: '',
    end_time: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (event) {
      setFormData({
        name: event.name,
        room_id: event.room_id,
        date: new Date(event.date + 'T00:00:00'),
        start_time: event.start_time,
        end_time: event.end_time,
      });
    } else if (rooms.length > 0) {
      setFormData(prev => ({ ...prev, room_id: rooms[0].id }));
    }
  }, [event, rooms]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const token = localStorage.getItem('token');
    const payload = {
      name: formData.name,
      room_id: formData.room_id,
      date: format(formData.date, 'yyyy-MM-dd'),
      start_time: formData.start_time,
      end_time: formData.end_time,
    };

    try {
      if (event) {
        // Update existing event
        await axios.put(`${API}/events/${event.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Impreza zaktualizowana pomyślnie');
      } else {
        // Create new event
        await axios.post(`${API}/events`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Impreza dodana pomyślnie');
      }
      onSave();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Nieprawidłowe dane');
      } else {
        toast.error('Wystąpił błąd podczas zapisywania imprezy');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="event-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {event ? 'Edytuj imprezę' : 'Dodaj nową imprezę'}
          </DialogTitle>
          <DialogDescription>
            {event ? 'Zaktualizuj szczegóły imprezy urodzinowej' : 'Zaplanuj nową imprezę urodzinową'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name">Imię solenizanta</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="np. Krzysztof"
              required
              data-testid="event-name-input"
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="room">Sala</Label>
            <Select
              value={formData.room_id}
              onValueChange={(value) => setFormData({ ...formData, room_id: value })}
            >
              <SelectTrigger className="h-11" data-testid="room-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rooms.map(room => (
                  <SelectItem key={room.id} value={room.id}>{room.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Data</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal h-11"
                  data-testid="date-picker-trigger"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(formData.date, 'd MMMM yyyy', { locale: pl })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.date}
                  onSelect={(date) => date && setFormData({ ...formData, date })}
                  locale={pl}
                  data-testid="date-picker-calendar"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_time">Godzina rozpoczęcia</Label>
              <Input
                id="start_time"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                required
                data-testid="start-time-input"
                className="h-11"
                step="60"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_time">Godzina zakończenia</Label>
              <Input
                id="end_time"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                required
                data-testid="end-time-input"
                className="h-11"
                step="60"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 h-11"
              disabled={loading}
              data-testid="cancel-button"
            >
              Anuluj
            </Button>
            <Button
              type="submit"
              className="flex-1 h-11 text-white font-semibold"
              style={{ background: '#6E35C0' }}
              disabled={loading}
              data-testid="save-event-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Zapisywanie...
                </>
              ) : (
                event ? 'Zaktualizuj' : 'Dodaj imprezę'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EventForm;
